import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Github, Twitter, Linkedin, Globe } from 'lucide-react';
import { Logo } from '@/components/ui';

const Footer = () => {
  const { t, i18n } = useTranslation();
  const currentYear = new Date().getFullYear();

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'es', label: 'Español' },
    { code: 'it', label: 'Italiano' },
  ];

  const currentLangCode = languages.find(
    (l) => i18n.language === l.code || i18n.language.startsWith(`${l.code}-`)
  )?.code || 'en';

  const cycleLanguage = () => {
    const currentIndex = languages.findIndex((l) => l.code === currentLangCode);
    const next = languages[(currentIndex + 1) % languages.length];
    i18n.changeLanguage(next.code);
  };

  const footerLinks = {
    product: {
      title: t('footer.product'),
      links: [
        { label: t('footer.features'), href: '#features' },
      ],
    },
    contact: {
      title: t('footer.contact'),
      links: [
        { label: 'contact@klipr.it', href: 'mailto:contact@klipr.it', external: true },
      ],
    },
    legal: {
      title: t('footer.legalSection'),
      links: [
        { label: t('footer.privacy'), href: '/privacy' },
        { label: t('footer.terms'), href: '/terms' },
        { label: t('footer.cookies'), href: '/cookies' },
        { label: t('footer.legalNotice'), href: '/legal' },
        { label: t('footer.reportAbuse'), href: '/report-abuse' },
      ],
    },
  };

  const socialLinks = [
    { icon: <Twitter className="w-5 h-5" />, href: 'https://twitter.com/klipr', label: 'Twitter' },
    { icon: <Github className="w-5 h-5" />, href: 'https://github.com/klipr', label: 'GitHub' },
    { icon: <Linkedin className="w-5 h-5" />, href: 'https://linkedin.com/company/klipr', label: 'LinkedIn' },
  ];

  return (
    <footer className="bg-white border-t border-gray-100 dark:bg-dark-900 dark:border-dark-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <Logo size="sm" />
            </Link>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
              {t('footer.tagline')}
            </p>
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-dark-800 dark:hover:text-white transition-colors"
                  aria-label={social.label}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          {Object.values(footerLinks).map((section) => (
            <div key={section.title}>
              <h4 className="font-semibold text-dark-800 dark:text-white mb-4">{section.title}</h4>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.href}>
                    {'external' in link && link.external ? (
                      <a
                        href={link.href}
                        className="text-gray-500 hover:text-dark-800 dark:text-gray-400 dark:hover:text-white transition-colors text-sm"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-gray-500 hover:text-dark-800 dark:text-gray-400 dark:hover:text-white transition-colors text-sm"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-gray-100 dark:border-dark-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            &copy; {currentYear} Klipr. {t('footer.allRightsReserved')}
          </p>
          <div className="flex items-center gap-4">
            <button
              onClick={cycleLanguage}
              className="flex items-center gap-1.5 text-gray-400 hover:text-dark-800 dark:hover:text-white transition-colors text-sm"
              aria-label="Change language"
            >
              <Globe className="w-4 h-4" />
              <span>{languages.find((l) => l.code === currentLangCode)?.label}</span>
            </button>
            <p className="text-gray-400 dark:text-gray-500 text-sm">
              {t('footer.madeWithLove')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
